import * as React from 'react';
import { useLocation } from "react-router-dom";
import UserModel from '@app/Model/UserModel';
import UserAuthentication from '@app/Security/UserAuthentication';
import PageHelper from '@app/Service/PageHelper';
import PageModel from '@app/Model/PageModel';
import PageInterface from '@app/Interface/PageInterface';
import PageLoader from '@app/Page/PageLoader';
import GeneralPage from '@app/Page/GeneralPage';
import ContactListPage from '@app/Page/ContactListPage';
import ContactFormPage from '@app/Page/ContactFormPage';
import MyAccountPage from '@app/Page/MyAccountPage';
import ChangePasswordPage from '@app/Page/ChangePasswordPage';
import ArticlePage from '@app/Page/ArticlePage';
import EventPage from '@app/Page/EventPage';
import ReportPage from '@app/Page/ReportPage';
import ChartPage from '@app/Page/ChartPage';
import { NotFound } from '@app/NotFound/NotFound';
import ToastNotifications from '@app/Components/ToastNotification/ToastNotifications';
import { UserContext } from '@app/Context/UserContext';
import UserInterface from '@app/Interface/UserInterface';
import TypeAssertions from '@app/utils/TypeAssertions';
import PageAlerts from '@app/Components/PageAlerts';
import TwoFactorSettingsPage from './TwoFactorSettingsPage';
import { Dashboard } from '@app/Dashboard/Dashboard';
import MyFpuListPage from './MyFpuListPage';
import TemplateDefinitions from '@app/utils/TemplateDefinitions';
import EzFeedPage from './EzFeedPage';
import NotificationsPage from './NotificationsPage';
import { PermissionDenied } from '@app/PermissionDenied/PermissionDenied';
import AnnualMeetingRegPage from './AnnualMeetingRegPage';

// see: https://stackoverflow.com/a/61669105/8038146
//      The pageId is passed as a state value in navLink to={}
//      see src\app\Components\Navigation\Navigation.tsx -> const renderNavItem
interface LocationState {
    pageId: number,
    currentUser: UserModel
}

const PageTemplate: React.FC = () => {

    const location = useLocation<LocationState>();

    const { userState } = React.useContext(UserContext);

    const [currentUser, setCurrentUser] = React.useState<UserInterface>(new UserModel());

    const [currentPath, setCurrentPath] = React.useState("");

    const [canUserViewPage, setCanUserViewPage] = React.useState(true);

    const [pageData, setPageData] = React.useState<PageInterface>(new PageModel());

    const [updatePageCacheInBackground, setUpdatePageCacheInBackground] = React.useState(false);

    // set defaults
    React.useEffect(() => {

        // whenever the path changes reset defaults
        setPageData(new PageModel());

        setCurrentPath(location.pathname);

        setCanUserViewPage(true);

    }, [location.pathname]);

    // check for current user
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        /**
         * gets the authenticated user by 
         * getting the URI from a cookie, which 
         * if page is accessed directly
         * TODO: Replace with persistent usercontext 
         */
        async function getAuthenticatedUser() {

            const userIri = UserAuthentication.getUserLoggedInIri();

            const user = await UserAuthentication.getUser(controller, userIri);

            if (mounted) {
                setCurrentUser(user);
            }

        }

        if (userState.user.id > 0) {
            setCurrentUser(userState.user);
        } else {
            getAuthenticatedUser();
        }

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [userState.user]);

    // get page data based on current path. Page ID no longer needed!
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        async function getPageData() {

            const pageData = await PageHelper.getPageDataByPath(controller, currentUser.currentFpuId, currentPath);

            if (pageData.id === 0) {
                pageData.id = -1;
                pageData.title = "Page Not Found";
                pageData.template = "not-found";
            }

            if (mounted) {
                setPageData(pageData);
                setUpdatePageCacheInBackground(true);
            }

        }

        // only get page data if we have a path
        if (!TypeAssertions.isStringNullOrEmpty(currentPath)) {
            getPageData();
        }

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [currentUser.currentFpuId, currentPath]);

    // Update page cache in background
    React.useEffect(() => {

        if (updatePageCacheInBackground) {

            PageHelper.getPageDataByPathNonAsync(currentUser.currentFpuId, currentPath);
            
            setUpdatePageCacheInBackground(false);
        }

    }, [currentPath, currentUser.currentFpuId, updatePageCacheInBackground]);

    // Check permission
    React.useEffect(() => {

        if (pageData.id > 0 && currentUser.id > 0) {

            // everyone can see the dashboard
            if (pageData.path === "/") {

                setCanUserViewPage(true);

            } else {

                const canView = UserAuthentication.canUserViewPage(currentUser, currentUser.currentFpuId, pageData.id);

                setCanUserViewPage(canView);

            }

        }

    }, [currentUser, currentUser.currentFpuId, pageData.id, pageData.path]);

    if (canUserViewPage === false) {
        return (
            <PermissionDenied />
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let pageContent: any;

    // check for dashboard. which is always the root path
    if (pageData.path === "/") {

        pageContent = <Dashboard pageData={pageData} />

    } else {

        switch (pageData.template) {
            case "":
                pageContent = <GeneralPage pageData={pageData} />
                break;

            case "contact_form_template.php":
                pageContent = <ContactFormPage pageData={pageData} currentUser={currentUser} />
                break;

            case "my_account_template.php":
                pageContent = <MyAccountPage pageData={pageData} currentUser={currentUser} />
                break;

            case "change_password_template.php":
                pageContent = <ChangePasswordPage pageData={pageData} currentUser={currentUser} />
                break;

            case "articles_template.php":
                pageContent = <ArticlePage pageData={pageData} path={location.pathname} search={location.search} fpuId={currentUser.currentFpuId} />
                break;

            case "events_template.php":
                pageContent = <EventPage pageData={pageData} path={location.pathname} search={location.search} fpuId={currentUser.currentFpuId} currentUser={currentUser} />
                break;

            case "contact_list_template.php":
                pageContent = <ContactListPage pageData={pageData} />
                break;

            case "report_template.php":
                pageContent = <ReportPage pageData={pageData} />
                break;

            case "chart_template.php":
                pageContent = <ChartPage pageData={pageData} />
                break;

            case TemplateDefinitions.TEMPLATE_VIEW_FPU_LIST_PAGE:
                pageContent = <MyFpuListPage pageData={pageData} currentUser={currentUser} />
                break;

            case "two_factor_settings_template.php":
                pageContent = <TwoFactorSettingsPage pageData={pageData} currentUser={currentUser} />
                break;

            case "ez_feed_template.php":
                pageContent = <EzFeedPage pageData={pageData} />
                break;

            case "notifications_template.php":
                pageContent = <NotificationsPage pageData={pageData} currentUser={currentUser} />
                break;
            
            case "annual_meeting_reg_template.php":
                pageContent = <AnnualMeetingRegPage pageData={pageData} currentUser={currentUser} />
                break;

            case "not-found":
            default:
                pageContent = <NotFound />
                break;

        }

    }

    return (
        <>
            {pageData.id === 0
                ? <PageLoader />
                : <ToastNotifications>
                    <PageAlerts pageId={pageData.id}>
                        {pageContent}
                    </PageAlerts>
                </ToastNotifications>
            }
        </>
    )

}

export { PageTemplate };